import PropTypes from 'prop-types';
import type {
	BoundActions,
	HookFunction,
	OverrideContainerComponent,
	SubscriberComponent,
} from 'react-sweet-state';
import { createStore, createHook, createSubscriber, createContainer } from 'react-sweet-state';

export type PageStateType = {
	/**
	 * Unique ID of content currently being viewed
	 */
	contentId?: string;
	/**
	 * Type of the content
	 */
	contentType?: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed' | 'slide' | 'folder';
	/**
	 * Determines whether contentId is loading
	 */
	contentIdLoading?: boolean;
	/**
	 * Page title without special characters, and with spaces replaced by "+", it's optional parameter
	 */
	contentSlug?: string;
	/**
	 * Control which version should be rendered for embedded content (attachments, "page-include" macro, etc.)
	 */
	embeddedContentRender?: string;
	/**
	 * Hash in URL, prefixed with "#"
	 * @deprecated Please use RoutesContext instead
	 */
	queryHash?: string;
	/**
	 * One of names of routes listed in `@confluence/named-routes` package
	 * @deprecated Please use RoutesContext instead
	 */
	routeName?: string;
	/**
	 * Unique key for the current space
	 */
	spaceKey?: string;
	/**
	 * Unique ID of the current space. Only populated on the Access Link route, where the viewer is
	 * not a member of the space and `useSpaceId()` cannot resolve it; everywhere else this is
	 * undefined. Callers that want the normal value should use `useSpaceId()` from
	 * `@confluence/space-utils`.
	 */
	spaceId?: string;
	/**
	 * Override of version to render
	 */
	versionOverride?: number;
	/**
	 * Determines whether current page is rendered in lite mode
	 */
	isLite?: boolean;
};

export type ContentType = PageStateType['contentType'];

/**
 * Only used in Screen.js
 * @deprecated Please use typescript instead
 */
export const pageStateShape: PropTypes.Requireable<
	PropTypes.InferProps<{
		contentId: PropTypes.Requireable<NonNullable<string | symbol | null | undefined>>;
		contentIdLoading: PropTypes.Requireable<boolean>;
		contentSlug: PropTypes.Requireable<string>;
		embeddedContentRender: PropTypes.Requireable<string>;
		queryHash: PropTypes.Requireable<string>;
		routeName: PropTypes.Requireable<string>;
		spaceKey: PropTypes.Requireable<string>;
		versionOverride: PropTypes.Requireable<number>;
		searchSessionId: PropTypes.Requireable<string>;
		isLite: PropTypes.Requireable<boolean>;
		contentType: PropTypes.Requireable<string>;
	}>
> = PropTypes.shape({
	contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.symbol]),
	contentIdLoading: PropTypes.bool,
	contentSlug: PropTypes.string,
	embeddedContentRender: PropTypes.string,
	queryHash: PropTypes.string,
	routeName: PropTypes.string,
	spaceKey: PropTypes.string,
	versionOverride: PropTypes.number,
	searchSessionId: PropTypes.string,
	isLite: PropTypes.bool,
	contentType: PropTypes.oneOf([
		'page',
		'blogpost',
		'whiteboard',
		'database',
		'embed',
		'slide',
		'folder',
	]),
});

type PageActions = {
	setPageStore: (pageState: PageStateType) => ({ setState }: { setState: any }) => void;
};

const actions: PageActions = {
	setPageStore:
		(pageState) =>
		({ setState }) =>
			setState(pageState),
};

export const initialPageState = {
	contentId: undefined,
	contentType: undefined,
	contentIdLoading: false,
	contentSlug: undefined,
	embeddedContentRender: undefined,
	queryHash: undefined,
	routeName: undefined,
	spaceKey: undefined,
	spaceId: undefined,
	versionOverride: undefined,
	isLite: false,
};

const PageStore = createStore<PageStateType, PageActions>({
	name: 'PageStore',
	initialState: initialPageState,
	actions,
});

// TODO: Update this to use latest container once react-sweet-state is updated to v2.7.0 or above
export const PageStateContainer: OverrideContainerComponent<PageStateType> = createContainer(
	PageStore,
	{
		onInit:
			() =>
			({ setState }, pageState: PageStateType) => {
				setState(pageState);
			},
		onUpdate:
			() =>
			({ setState }, pageState: PageStateType) => {
				setState(pageState);
			},
	},
);
export const PageSubscriber: SubscriberComponent<
	PageStateType,
	BoundActions<PageStateType, PageActions>,
	unknown
> = createSubscriber(PageStore);

export const usePageState: HookFunction<
	PageStateType,
	BoundActions<PageStateType, PageActions>,
	void
> = createHook(PageStore);

export const usePageContentId: HookFunction<
	string | undefined,
	BoundActions<PageStateType, PageActions>,
	void
> = createHook(PageStore, {
	selector: (state) => state.contentId,
});

export const usePageSpaceKey: HookFunction<
	string | undefined,
	BoundActions<PageStateType, PageActions>,
	void
> = createHook(PageStore, {
	selector: (state) => state.spaceKey,
});

export const usePageSpaceId: HookFunction<
	string | undefined,
	BoundActions<PageStateType, PageActions>,
	void
> = createHook(PageStore, {
	selector: (state) => state.spaceId,
});

export const useContentType: HookFunction<
	'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed' | 'slide' | 'folder' | undefined,
	BoundActions<PageStateType, PageActions>,
	void
> = createHook(PageStore, {
	selector: (state) => state.contentType,
});
